import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useClients } from '../api/queries/clients';
import { useCreateProject, type CreateProjectInput } from '../api/queries/projects';
import { ApiError } from '../api/client';
import { can } from '../auth/store';
import type { CaseType, ProjectStatus } from '@stfors/shared-types';
import { LookupSelect } from '../components/LookupSelect';
import { MoneyInput } from '../components/MoneyInput';
import { PersonPicker } from '../components/PersonPicker';
import { AddClientModal, ADD_CLIENT_VALUE } from '../components/AddClientModal';
import './NewProjectPage.css';

// Case type / status / billing type are admin-managed under Settings → Lookups
// (categories 'case_type', 'project_status', 'billing_type') — see <LookupSelect>.

export function NewProjectPage(): JSX.Element {
  const navigate = useNavigate();
  const create = useCreateProject();
  const { data: clientsData, isPending: clientsLoading, error: clientsError } = useClients();

  const [clientId, setClientId] = useState<number>(0);
  // case_number is allocated by the server (see api/routes/projects.ts).
  // We don't collect it in the form; clients can't infer prior case
  // volume from a sequential number.
  const [caseType, setCaseType] = useState<CaseType>('civil_litigation');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [status, setStatus] = useState<ProjectStatus>('new');
  const [priority, setPriority] = useState<1 | 2 | 3 | 4 | 5>(3);
  const [billingType, setBillingType] = useState<CreateProjectInput['billing_type']>('hourly');
  const [flatFeeAmount, setFlatFeeAmount] = useState('');
  const [jurisdiction, setJurisdiction] = useState('');
  // The attorney is a directory person, not typed prose. `rep_person_id` is
  // what the BILL TO block reads (migration 0054); `leadAttorney` keeps the
  // display-name snapshot for the readers that predate it — the notification
  // strings, the client portal projection, the project header.
  const [attorneyId, setAttorneyId] = useState<number | null>(null);
  const [leadAttorney, setLeadAttorney] = useState('');

  const [topError, setTopError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [addClientOpen, setAddClientOpen] = useState(false);
  // A client created from here isn't in the cached list yet, so the <option>
  // that keeps the select showing its name has to come from state.
  const [newClient, setNewClient] = useState<{ id: number; name: string } | null>(null);

  if (clientsLoading) {
    return <p className="np-status">Loading clients…</p>;
  }
  if (clientsError) {
    return <p className="np-status np-error">Could not load the clients list.</p>;
  }
  const clients = clientsData?.clients ?? [];
  if (clients.length === 0) {
    return (
      <div className="np-noclients">
        <h2>No clients yet</h2>
        <p>
          A project belongs to a client. Create one first, then come back.
        </p>
        <Link to="/clients/new" className="btn-cyan-primary">
          Create a client
        </Link>
      </div>
    );
  }

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setTopError(null);
    setFieldErrors({});

    if (clientId <= 0) {
      setFieldErrors({ client_id: 'Select a client' });
      return;
    }

    const payload: CreateProjectInput = {
      client_id: clientId,
      // case_number intentionally omitted — allocated server-side.
      case_type: caseType,
      title: title.trim(),
      description: description.trim() || undefined,
      status,
      priority,
      billing_type: billingType,
      flat_fee_amount: billingType === 'flat_fee' ? flatFeeAmount.trim() : undefined,
      jurisdiction: jurisdiction.trim() || undefined,
      lead_attorney: leadAttorney.trim() || undefined,
      ...(attorneyId !== null ? { rep_person_id: attorneyId } : {}),
    };

    try {
      await create.mutateAsync(payload);
      navigate('/projects');
    } catch (err) {
      if (err instanceof ApiError && err.status === 400) {
        const body = err.body as
          | { details?: { fieldErrors?: Record<string, string[]> } }
          | null;
        const fe = body?.details?.fieldErrors;
        if (fe) {
          const flat: Record<string, string> = {};
          for (const [k, v] of Object.entries(fe)) {
            if (Array.isArray(v) && v.length > 0) flat[k] = v[0]!;
          }
          setFieldErrors(flat);
          setTopError('Please fix the highlighted fields.');
          return;
        }
      }
      if (err instanceof ApiError && err.status === 403) {
        setTopError("You don't have permission to create projects.");
        return;
      }
      setTopError('Could not create the project. Please try again.');
    }
  };

  const isFlatFee = billingType === 'flat_fee';

  return (
    <section className="np-section">
      <header className="np-header">
        <h2>New Project</h2>
        <button
          type="button"
          className="btn-text"
          onClick={() => navigate('/projects')}
        >
          ← Back to projects
        </button>
      </header>

      <form className="np-form" onSubmit={onSubmit} noValidate>
        <div className="np-row">
          <label className="np-field">
            <span>Client *</span>
            <select
              className="form-input"
              value={clientId}
              onChange={(e) => {
                if (e.target.value === ADD_CLIENT_VALUE) { setAddClientOpen(true); return; }
                setClientId(Number(e.target.value));
              }}
              required
            >
              <option value={0}>Select a client…</option>
              {clients.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
              {/* Not in the cached list yet, so it carries its own option. */}
              {newClient && !clients.some((c) => c.id === newClient.id) && (
                <option value={newClient.id}>{newClient.name}</option>
              )}
              {can('client.write') && (
                <option value={ADD_CLIENT_VALUE}>Other — add a new client…</option>
              )}
            </select>
            {fieldErrors['client_id'] && (
              <span className="field-error">{fieldErrors['client_id']}</span>
            )}
          </label>

          <label className="np-field">
            <span>Case number</span>
            <input
              type="text"
              className="form-input"
              value="(generated on save)"
              disabled
              readOnly
              title="The case number is generated automatically and revealed once the project is saved. It uses random letters so clients can't infer how many prior cases the firm has handled."
            />
          </label>
        </div>

        <label className="np-field np-field-full">
          <span>Title *</span>
          <input
            type="text"
            className="form-input"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            required
            maxLength={500}
          />
          {fieldErrors['title'] && (
            <span className="field-error">{fieldErrors['title']}</span>
          )}
        </label>

        <div className="np-row">
          <label className="np-field">
            <span>Case type *</span>
            <LookupSelect
              category="case_type"
              value={caseType}
              onChange={setCaseType}
              aria-label="Case type"
            />
          </label>

          <label className="np-field">
            <span>Status</span>
            <LookupSelect
              category="project_status"
              value={status}
              onChange={setStatus}
              aria-label="Status"
            />
          </label>
        </div>

        <div className="np-row">
          <label className="np-field">
            <span>Priority (1=highest)</span>
            <select
              className="form-input"
              value={priority}
              onChange={(e) =>
                setPriority(Number(e.target.value) as 1 | 2 | 3 | 4 | 5)
              }
            >
              {[1, 2, 3, 4, 5].map((n) => (
                <option key={n} value={n}>
                  P{n}
                </option>
              ))}
            </select>
          </label>

          <label className="np-field">
            <span>Billing type *</span>
            <LookupSelect
              category="billing_type"
              value={billingType}
              onChange={setBillingType}
              aria-label="Billing type"
            />
          </label>
        </div>

        {isFlatFee && (
          <label className="np-field np-field-full">
            <span>Flat fee amount * (USD)</span>
            <MoneyInput
              value={flatFeeAmount}
              onChange={setFlatFeeAmount}
              placeholder="12500.00"
              required
            />
            {fieldErrors['flat_fee_amount'] && (
              <span className="field-error">{fieldErrors['flat_fee_amount']}</span>
            )}
          </label>
        )}

        <div className="np-row">
          <label className="np-field">
            <span>Jurisdiction</span>
            <input
              type="text"
              className="form-input"
              value={jurisdiction}
              onChange={(e) => setJurisdiction(e.target.value)}
              maxLength={200}
            />
          </label>

          <div className="np-field">
            <span>Lead attorney</span>
            <PersonPicker
              value={attorneyId}
              onChange={(id, person) => {
                setAttorneyId(id);
                setLeadAttorney(person?.displayName ?? '');
              }}
              placeholder="Search people…"
              allowCreate={can('organization.write')}
            />
            <span className="np-hint">
              Their firm comes from the client&apos;s representative; set a different
              one on the project page.
            </span>
          </div>
        </div>

        <label className="np-field np-field-full">
          <span>Description</span>
          <textarea
            className="form-input np-textarea"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={4}
            maxLength={10000}
          />
        </label>

        {topError && (
          <p className="np-error-banner" role="alert">
            {topError}
          </p>
        )}

        <div className="np-actions">
          <button
            type="button"
            className="btn-charcoal-secondary"
            onClick={() => navigate('/projects')}
            disabled={create.isPending}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn-cyan-primary"
            disabled={
              create.isPending ||
              clientId <= 0 ||
              title.trim().length === 0 ||
              (isFlatFee && flatFeeAmount.trim().length === 0)
            }
          >
            {create.isPending ? 'Creating…' : 'Create project'}
          </button>
        </div>
      </form>

      {addClientOpen && (
        <AddClientModal
          open
          onClose={() => setAddClientOpen(false)}
          onCreated={(id, name) => {
            setNewClient({ id, name });
            setClientId(id);
            setFieldErrors((f) => { const { client_id: _drop, ...rest } = f; return rest; });
            setAddClientOpen(false);
          }}
        />
      )}
    </section>
  );
}
